import React from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { PrinterIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Seo } from '../../components/Seo';
import { findCertificate, useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
export function AcademyCertificate() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    state
  } = useAcademy();
  const {
    courses
  } = useAcademyHub();
  const found = findCertificate(state, id ?? '', courses);
  const verifyUrl = `https://academy.cavalladata.com/verify/${id}`;
  if (!found) {
    return <Container className="py-24" data-id="element-1545" data-mp="2kux2eabofo0a">
        <Seo title="Certificate not found, Cavalla Academy" description="Certificate verification." path={`/academy/certificates/${id}`} noIndex data-id="element-1546" data-mp="2kvpy8g93kt3n" />
        <h1 className="font-display text-[30px] text-ink" data-id="element-1547" data-mp="j20irz3pbd4k">We could not find that certificate</h1>
        <p className="mt-3 max-w-xl text-[16px] text-body" data-id="element-1548" data-mp="2ebyxtsw1mxb2">
          Certificates are shown on the device where the course was completed. Public verification at academy.cavalladata.com/verify
          opens when the Academy server goes live.
        </p>
        <Button to="/academy/dashboard" variant="secondary" className="mt-8" data-id="element-1549" data-mp="24gf51fcs0eqj">
          Go to my learning
        </Button>
      </Container>;
  }
  const {
    cert,
    course
  } = found;
  const issued = new Date(cert.issuedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return <>
      <Seo title={`Certificate, ${course.title}`} description={`${cert.learnerName} completed ${course.title}.`} path={`/academy/certificates/${cert.id}`} noIndex data-id="element-1550" data-mp="2nd6ih85mzzmo" />
      <Container className="py-12 sm:py-16" data-id="element-1551" data-mp="2s9x0i11055aq">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 print:hidden" data-id="element-1552" data-mp="3g1c6q4vwg6r9">
          <h1 className="font-display text-[24px] text-ink" data-id="element-1553" data-mp="3ksuaf0pb4lfx">Your certificate</h1>
          <Button type="button" variant="secondary" onClick={() => window.print()} data-id="element-1554" data-mp="3uoayy22kz571">
            <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-1555" data-mp="3ulx15lginevs" />
            Print or save as PDF
          </Button>
        </div>

        <article aria-label="Certificate of completion" className="relative mx-auto aspect-[1.414/1] w-full max-w-4xl overflow-hidden rounded-md bg-[#1B2D4E] text-white print:rounded-none" data-id="element-1556" data-mp="3ln38252e96mz">
          <div className="absolute inset-y-0 left-0 w-3 bg-[#E8752C]" aria-hidden="true" data-id="element-1557" data-mp="3bqgnzihw6jm0" />
          <div className="flex h-full flex-col justify-between p-[6%] pl-[8%]" data-id="element-1558" data-mp="36ygmvmq35knu">
            <div className="flex items-center gap-3" data-id="element-1559" data-mp="31z3y2wfnm3im">
              <span className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#E8752C] font-display text-[17px] font-semibold" data-id="element-1560" data-mp="31zvidb3swetl">C</span>
              <span className="font-display text-[15px] sm:text-[17px]" data-id="element-1561" data-mp="2szgtk1rmohlc">Cavalla Academy</span>
            </div>
            <div data-id="element-1562" data-mp="2nzkr46ykwy7w">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E8752C] sm:text-[13px]" data-id="element-1563" data-mp="2o15kht95qb46">Certificate of completion</p>
              <p className="mt-3 font-display text-[clamp(1.6rem,4.5vw,3rem)] leading-tight" data-id="element-1564" data-mp="13k69eco54eze">{cert.learnerName}</p>
              <p className="mt-3 text-[13px] text-white/70 sm:text-[16px]" data-id="element-1565" data-mp="1dg98f9np3xi4">has completed</p>
              <p className="mt-1 max-w-[80%] font-display text-[clamp(1rem,2.4vw,1.6rem)] leading-snug" data-id="element-1566" data-mp="1hhl3hupc9axp">{course.title}</p>
            </div>
            <div className="flex items-end justify-between gap-6" data-id="element-1567" data-mp="ksdgei92mtro">
              <dl className="grid gap-1 text-[10px] text-white/70 sm:text-[13px]" data-id="element-1568" data-mp="pq5eju0b7c4b">
                <div data-id="element-1569" data-mp="ts0fgxebvnhk">
                  <dt className="inline" data-id="element-1570" data-mp="trgwddcxyt1y">Issued </dt>
                  <dd className="inline text-white" data-id="element-1571" data-mp="tpw27oujhc4m">{issued}</dd>
                </div>
                <div data-id="element-1572" data-mp="yka0q04hzzdi">
                  <dt className="inline" data-id="element-1573" data-mp="ykjttwkuamnf">Certificate ID </dt>
                  <dd className="inline break-all font-mono text-white" data-id="element-1574" data-mp="201c3pyuxiew">{cert.id}</dd>
                </div>
              </dl>
              <div className="shrink-0 rounded-[6px] bg-white p-1.5 sm:p-2" data-id="element-1575" data-mp="60l6wmvyk9xv">
                <QRCodeSVG value={verifyUrl} size={88} className="h-14 w-14 sm:h-[88px] sm:w-[88px]" aria-label={`Verification code for ${verifyUrl}`} data-id="element-1576" data-mp="3j3yq1kixd9au" />
              </div>
            </div>
          </div>
        </article>
        <p className="mx-auto mt-4 max-w-4xl text-small text-muted print:hidden" data-id="element-1577" data-mp="3sv37qrzzj89d">
          The QR code points to {verifyUrl}. Public verification goes live with the Academy server.
        </p>
      </Container>
    </>;
}